import AbstractModel from './AbstractModel';
import { STATUS } from '../../utils/constants';
import UserModel from './UserModel';
import ProjectModel from './Project/ProjectModel';
// eslint-disable-next-line import/no-cycle
import TicketModel from './TicketModel';
// eslint-disable-next-line import/no-cycle
import ProcurementModel from './ProcurementModel';

class InternalRequestModel extends AbstractModel {
  static TYPES = Object.freeze({
    EXPENSE_REIMBURSEMENT: 'expenseReimbursement',
    INTEREST_FREE_LOAN: 'interestFreeLoan',
    CASH_ADVANCE: 'cashAdvance',
    EXTRA_WORK_APPROVAL: 'extraWorkApproval',
    OVERTIME_WORK_REQUEST: 'overtimeWorkRequest',
    MATERIALS_REQUEST: 'materialsRequest',
    FINANCIAL_REQUEST: 'financialRequest',
    LEAVE_REQUEST: 'leaveRequest',
  });

  // Subset of TYPES that can be created today (mirrors the create-IR dialog
  // options). Legacy values (interestFreeLoan, cashAdvance, overtimeWorkRequest)
  // are kept in TYPES for historic records but excluded from filters / pickers.
  static CREATABLE_TYPES = Object.freeze([
    InternalRequestModel.TYPES.EXPENSE_REIMBURSEMENT,
    InternalRequestModel.TYPES.MATERIALS_REQUEST,
    InternalRequestModel.TYPES.EXTRA_WORK_APPROVAL,
    InternalRequestModel.TYPES.FINANCIAL_REQUEST,
    InternalRequestModel.TYPES.LEAVE_REQUEST,
  ]);

  static STATUSES = Object.freeze({
    DRAFT: 'draft',
    OPENED: 'opened',
    APPROVED: 'approved',
    PENDING_CLOSED: 'pendingClosed',
    REJECTED: 'rejected',
    CLOSED: 'closed',
    ALL: 'all',
  });

  static PAYMENT_METHODS = Object.freeze({
    COMPANY_CARD: 'companyCard',
    PERSONAL_CASH_CARD: 'personalCashCard',
    COMPANY_CASH: 'companyCash',
  });

  static COST_CATEGORIES = Object.freeze({
    TRAVEL: 'travel',
    ACCOMMODATION: 'accommodation',
    MATERIALS: 'materials',
    SERVICES: 'services',
    EQUIPMENT: 'equipment',
    OTHER: 'other',
  });

  static UNITS_OF_MEASURE = {
    PIECE: 'piece',
    HOUR: 'hour',
    DAY: 'day',
    MONTH: 'month',
    KILOGRAM: 'kilogram',
    GRAM: 'gram',
    LITER: 'liter',
    METER: 'meter',
    SQUARE_METER: 'squareMeter',
    CUBIC_METER: 'cubicMeter',
    PACKAGE: 'package',
    BOX: 'box',
    SET: 'set',
  };

  static READABLE_UNIT_OF_MEASURE = {
    [InternalRequestModel.UNITS_OF_MEASURE.PIECE]:
      'internalRequest.unitOfMeasure.piece',
    [InternalRequestModel.UNITS_OF_MEASURE.HOUR]:
      'internalRequest.unitOfMeasure.hour',
    [InternalRequestModel.UNITS_OF_MEASURE.DAY]:
      'internalRequest.unitOfMeasure.day',
    [InternalRequestModel.UNITS_OF_MEASURE.MONTH]:
      'internalRequest.unitOfMeasure.month',
    [InternalRequestModel.UNITS_OF_MEASURE.KILOGRAM]:
      'internalRequest.unitOfMeasure.kilogram',
    [InternalRequestModel.UNITS_OF_MEASURE.GRAM]:
      'internalRequest.unitOfMeasure.gram',
    [InternalRequestModel.UNITS_OF_MEASURE.LITER]:
      'internalRequest.unitOfMeasure.liter',
    [InternalRequestModel.UNITS_OF_MEASURE.METER]:
      'internalRequest.unitOfMeasure.meter',
    [InternalRequestModel.UNITS_OF_MEASURE.SQUARE_METER]:
      'internalRequest.unitOfMeasure.squareMeter',
    [InternalRequestModel.UNITS_OF_MEASURE.CUBIC_METER]:
      'internalRequest.unitOfMeasure.cubicMeter',
    [InternalRequestModel.UNITS_OF_MEASURE.PACKAGE]:
      'internalRequest.unitOfMeasure.package',
    [InternalRequestModel.UNITS_OF_MEASURE.BOX]:
      'internalRequest.unitOfMeasure.box',
    [InternalRequestModel.UNITS_OF_MEASURE.SET]:
      'internalRequest.unitOfMeasure.set',
  };

  static READABLE_STATUS = Object.freeze({
    [InternalRequestModel.STATUSES.DRAFT]:
      'internalRequest.attributes.status.draft',
    [InternalRequestModel.STATUSES.OPENED]:
      'internalRequest.attributes.status.opened',
    [InternalRequestModel.STATUSES.APPROVED]:
      'internalRequest.attributes.status.approved',
    [InternalRequestModel.STATUSES.PENDING_CLOSED]:
      'internalRequest.attributes.status.pendingClosed',
    [InternalRequestModel.STATUSES.REJECTED]:
      'internalRequest.attributes.status.rejected',
    [InternalRequestModel.STATUSES.CLOSED]:
      'internalRequest.attributes.status.closed',
    [InternalRequestModel.STATUSES.ALL]:
      'internalRequest.attributes.status.all',
  });

  static READABLE_TYPE = Object.freeze({
    [InternalRequestModel.TYPES.EXPENSE_REIMBURSEMENT]:
      'internalRequest.attributes.type.expenseReimbursement',
    [InternalRequestModel.TYPES.INTEREST_FREE_LOAN]:
      'internalRequest.attributes.type.interestFreeLoan',
    [InternalRequestModel.TYPES.CASH_ADVANCE]:
      'internalRequest.attributes.type.cashAdvance',
    [InternalRequestModel.TYPES.EXTRA_WORK_APPROVAL]:
      'internalRequest.attributes.type.extraWorkApproval',
    [InternalRequestModel.TYPES.OVERTIME_WORK_REQUEST]:
      'internalRequest.attributes.type.overtimeWorkRequest',
    [InternalRequestModel.TYPES.MATERIALS_REQUEST]:
      'internalRequest.attributes.type.materialsRequest',
    [InternalRequestModel.TYPES.FINANCIAL_REQUEST]:
      'internalRequest.attributes.type.financialRequest',
    [InternalRequestModel.TYPES.LEAVE_REQUEST]:
      'internalRequest.attributes.type.leaveRequest',
  });

  static FINANCIAL_REQUEST_SUB_TYPES = {
    LOAN_REQUEST: 'loanRequest',
    SALARY_ADVANCE: 'salaryAdvance',
    LEAVE_PAYMENT: 'leavePayment',
    RECRUITMENT_BONUS: 'recruitmentBonus',
  };

  constructor(...params) {
    super(...params);
    // Override the AbstractModel's type with the actual request type from data
    const attributes = params[0] || {};
    if (attributes.type) {
      this._requestType = attributes.type;
    }
    if (this.status !== STATUS.LOADING) {
      this.createdAt &&
        this.overwriteProperty('createdAt', {
          get() {
            return this._createdAt ? new Date(this._createdAt) : null;
          },
        });
      this.updatedAt &&
        this.overwriteProperty('updatedAt', {
          get() {
            return this._updatedAt ? new Date(this._updatedAt) : null;
          },
        });
      this.submittedAt &&
        this.overwriteProperty('submittedAt', {
          get() {
            return this._submittedAt ? new Date(this._submittedAt) : null;
          },
        });
      this.approvedAt &&
        this.overwriteProperty('approvedAt', {
          get() {
            return this._approvedAt ? new Date(this._approvedAt) : null;
          },
        });
      this.rejectedAt &&
        this.overwriteProperty('rejectedAt', {
          get() {
            return this._rejectedAt ? new Date(this._rejectedAt) : null;
          },
        });

      this.requestedBy = new UserModel(this.requestedBy);
      if (this.approver) {
        this.approver = new UserModel(this.approver);
      }
      this.project = new ProjectModel(this.project);

      if (this.linkedTicket) {
        this.linkedTicket = new TicketModel(this.linkedTicket);
      }
      if (this.linkedProcurement) {
        this.linkedProcurement = new ProcurementModel(this.linkedProcurement);
      }
    }
  }

  convertAttribute(attribute) {
    if (attribute === 'status') {
      return 'internalRequestStatus';
    }
    return super.convertAttribute(attribute);
  }

  get readableStatus() {
    return (
      InternalRequestModel.READABLE_STATUS[this.internalRequestStatus] || '-'
    );
  }

  get requestType() {
    return this._requestType || null;
  }

  get readableType() {
    return InternalRequestModel.READABLE_TYPE[this._requestType] || '-';
  }

  get canEdit() {
    return this.internalRequestStatus === InternalRequestModel.STATUSES.DRAFT;
  }

  get canSubmit() {
    return this.internalRequestStatus === InternalRequestModel.STATUSES.DRAFT;
  }

  get canApprove() {
    return this.internalRequestStatus === InternalRequestModel.STATUSES.OPENED;
  }

  get canReject() {
    return this.internalRequestStatus === InternalRequestModel.STATUSES.OPENED;
  }
}

export default InternalRequestModel;
